import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import styles from '../../styles/views/components/TextArea.module.scss';

const getWidth = () => window.innerWidth
  || document.documentElement.clientWidth
  || document.body.clientWidth;

const TextArea = React.forwardRef(({
  name,
  value,
  label,
  labelType,
  placeholder,
  onChange,
  maxLength,
  isDisabled,
  resizeable,
  extraStyles,
  className,
  hideCounter,
  autoGrow,
  defaultHeight,
  maxHeight,
  error,
  ...textareaProps
}, ref) => {
  const [screenWidth, setScreenWidth] = useState(getWidth());

  useEffect(() => {
    // added debounce mechanism to limit function calls and improve performance
    let timeoutId = null;
    const resizeListener = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setScreenWidth(getWidth()), 150);
    };

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  useEffect(() => {
    if (autoGrow && ref && ref.current) {
      ref.current.style.height = extraStyles?.height || defaultHeight;
      const { scrollHeight } = ref.current;

      if (scrollHeight && maxHeight) {
        ref.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      } else {
        ref.current.style.height = `${scrollHeight}px`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, screenWidth, defaultHeight, ref?.current?.getBoundingClientRect?.()]);

  const labelClassName = labelType === 'compact'
    ? `${styles.compactLabel} ${error ? styles.compactErrorLabel : ''}`
    : styles.label;

  return (
    <>
      <div className={[styles.wrapper, className].join(' ')}>
        {label && <label className={labelClassName} htmlFor={name}>{label}</label>}
        <textarea
          id={name}
          name={name}
          value={value}
          style={{ ...extraStyles }}
          disabled={isDisabled}
          className={`${styles.field}  ${error ? styles.errorField : ''} ${resizeable ? styles.resizeableField : ''} ${autoGrow ? styles.autoGrowField : ''} `}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          {...textareaProps}
          ref={ref}
        />
        {!isDisabled && !hideCounter && <span className={styles.counter}>{value ? value.length : 0}/{maxLength}</span> }
        {error && (<span className={styles.errorLabel}>{error}</span>)}
      </div>
    </>
  );
});

TextArea.propTypes = {
  labelType: PropTypes.oneOf(['compact', 'classic']),
  extraStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.string,
  defaultHeight: PropTypes.string,
  isDisabled: PropTypes.bool,
  resizeable: PropTypes.bool,
  maxLength: PropTypes.number,
  className: PropTypes.string,
  hideCounter: PropTypes.bool,
  autoGrow: PropTypes.bool,
  maxHeight: PropTypes.number,
};

TextArea.defaultProps = {
  labelType: 'compact',
  extraStyles: null,
  placeholder: 'Type here',
  maxLength: 10000,
  isDisabled: false,
  resizeable: false,
  onChange: () => {},
  label: '',
  error: '',
  className: '',
  hideCounter: false,
  autoGrow: false,
  defaultHeight: '100px',
  maxHeight: 0,
};

export default TextArea;
